import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { putApartmentContent, updateApartmentMetadata } from '@/api/generated/sdk.gen';
import type {
  ApartmentContent as ApartmentContentDTO,
  UpdateApartmentMetadataRequest,
} from '@/api/generated/types.gen';
import type { Apartment } from '@/lib/apartments';
import { errorMessage } from '@/lib/errors';
import { apartmentContentQuery } from '@/lib/content';
import { useToast } from '@/components/ToastProvider';
import { rawBody, strOrNull } from '@/components/apartment/OverviewSection';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MarkdownEditor } from './ui/markdown-editor';

interface Props {
  apartmentID: string;
  apt: Apartment;
  canWrite: boolean;
}

// The nine descriptive sections. Each maps to a `<key>` text column and a
// `<key>_ota_sync` flag on the content DTO.
const SECTIONS = [
  { key: 'summary', label: 'Summary' },
  { key: 'the_space', label: 'The space' },
  { key: 'guest_access', label: 'Guest access' },
  { key: 'neighbourhood', label: 'Neighbourhood' },
  { key: 'getting_around', label: 'Getting around' },
  { key: 'directions', label: 'Directions' },
  { key: 'parking_info', label: 'Parking info' },
  { key: 'checkin_instructions', label: 'Check-in instructions' },
  { key: 'checkout_instructions', label: 'Check-out instructions' },
] as const;

type SectionKey = (typeof SECTIONS)[number]['key'];
type OtaKey = `${SectionKey}_ota_sync`;

// Local editable draft: every text section is a string (empty = cleared);
// every ota flag is a boolean. Wi-Fi credentials live in AccessOpsSection now
// (Access & ops tab) — this editor no longer owns them.
type Draft = Record<SectionKey, string> & Record<OtaKey, boolean>;

function draftFromDTO(c: ApartmentContentDTO | undefined): Draft {
  const d = {} as Draft;
  for (const { key } of SECTIONS) {
    d[key] = c?.[key] ?? '';
    d[`${key}_ota_sync`] = c?.[`${key}_ota_sync`] ?? false;
  }
  return d;
}

// Build the PUT body. Truly-empty strings are omitted so the server clears them
// to NULL (the PUT is a full replace; omitted/null text → cleared section). We
// test against '' (NOT .trim()) so an intentional whitespace value is sent
// verbatim rather than silently dropped.
//
// wifi_name/wifi_password are echoed back verbatim from the last-fetched
// snapshot (`data`) even though this editor no longer exposes them — they now
// belong to AccessOpsSection, but this PUT is a full-replace of the same row,
// so omitting them here would silently wipe the operator's Wi-Fi credentials.
function bodyFromDraft(d: Draft, data: ApartmentContentDTO | undefined) {
  const body: Record<string, string | boolean> = {};
  for (const { key } of SECTIONS) {
    if (d[key] !== '') body[key] = d[key];
    body[`${key}_ota_sync`] = d[`${key}_ota_sync`];
  }
  if (data?.wifi_name) body.wifi_name = data.wifi_name;
  if (data?.wifi_password) body.wifi_password = data.wifi_password;
  return body;
}

export function ApartmentContent({ apartmentID, apt, canWrite }: Props) {
  return (
    <div className="mt-6 space-y-6">
      <GuestTextSection apt={apt} canWrite={canWrite} />
      <ContentSectionsCard apartmentID={apartmentID} canWrite={canWrite} />
    </div>
  );
}

// GuestTextSection owns description_long / house_rules / amenities — columns
// on `apartments`, not `apartment_content`. It writes through
// updateApartmentMetadata (PATCH /apartments/{id}), a genuine partial patch
// (the backend only touches fields present in the JSON body), so this save
// sends ONLY these three keys — nickname/occupancy/etc (owned by
// OverviewSection) are never included, and an emptied field is sent as an
// explicit null (via strOrNull/rawBody) rather than omitted, since omitting a
// key here means "leave unchanged", not "clear".
//
// canWrite gates the Edit button here for the same reason OverviewSection
// gated it before these fields moved here: apartments.write, checked
// server-side by the same updateApartmentMetadata PATCH handler.
interface GuestTextDraft {
  description_long: string;
  house_rules: string;
  amenities: string; // comma-separated
}

function guestTextInitial(apt: Apartment): GuestTextDraft {
  return {
    description_long: apt.description_long ?? '',
    house_rules: apt.house_rules ?? '',
    amenities: apt.amenities.join(', '),
  };
}

function GuestTextSection({ apt, canWrite }: { apt: Apartment; canWrite: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<GuestTextDraft>(() => guestTextInitial(apt));
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = rawBody<UpdateApartmentMetadataRequest>({
        description_long: strOrNull(draft.description_long),
        house_rules: strOrNull(draft.house_rules),
        amenities: draft.amenities
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      return updateApartmentMetadata({ throwOnError: true, path: { id: apt.id }, body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartments', apt.id] });
      qc.invalidateQueries({ queryKey: ['apartments'] });
      setEditing(false);
      setSaveErr(null);
    },
    onError: (e) => setSaveErr(errorMessage(e)),
  });

  const startEdit = () => {
    setDraft(guestTextInitial(apt));
    setSaveErr(null);
    setEditing(true);
  };

  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">
          Description &amp; house rules
        </h2>
        {!editing && canWrite && (
          <Button variant="secondary" onClick={startEdit}>
            Edit description
          </Button>
        )}
      </div>

      {saveErr && (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {saveErr}
        </p>
      )}

      {editing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Input
            label="Description"
            multiline
            rows={4}
            value={draft.description_long}
            onChange={(e) => setDraft((d) => ({ ...d, description_long: e.target.value }))}
          />
          <Input
            label="House rules"
            multiline
            rows={3}
            className="mt-4"
            value={draft.house_rules}
            onChange={(e) => setDraft((d) => ({ ...d, house_rules: e.target.value }))}
          />
          <Input
            label="Amenities (comma-separated codes: wifi, kitchen, washer, …)"
            className="mt-4 font-mono"
            value={draft.amenities}
            onChange={(e) => setDraft((d) => ({ ...d, amenities: e.target.value }))}
          />
          <div className="mt-5 flex items-center gap-2">
            <Button type="submit" variant="primary" loading={save.isPending} disabled={save.isPending}>
              Save description
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <GuestTextRead apt={apt} />
      )}
    </section>
  );
}

function GuestTextRead({ apt }: { apt: Apartment }) {
  const hasAny = !!apt.description_long || !!apt.house_rules || apt.amenities.length > 0;
  if (!hasAny) {
    return <p className="text-sm text-muted">No description authored yet.</p>;
  }
  return (
    <>
      {apt.description_long && (
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Description</h3>
          <p className="whitespace-pre-line text-sm leading-relaxed">{apt.description_long}</p>
        </div>
      )}

      {apt.house_rules && (
        <div className="mt-5">
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">House rules</h3>
          <p className="whitespace-pre-line text-sm leading-relaxed">{apt.house_rules}</p>
        </div>
      )}

      {apt.amenities.length > 0 && (
        <div className="mt-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Amenities</h3>
          <ul className="flex flex-wrap gap-2">
            {apt.amenities.map((a) => (
              <li
                key={a}
                className="rounded-full border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-2.5 py-0.5 text-xs"
              >
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

// ContentSectionsCard owns the nine OTA-syncable markdown sections in
// `apartment_content` (PUT full-replace). Wi-Fi used to render here too; it
// now lives in AccessOpsSection (Access & ops tab) on the same row.
//
// canWrite gates the Edit button (existence, not just disabled) the same way
// AccessOpsSection and GuestTextSection do — apartments.write, checked
// server-side by the same putApartmentContent handler. Without this a
// read-only operator could open the editor and only discover they can't save
// after hitting the server's 403.
function ContentSectionsCard({ apartmentID, canWrite }: { apartmentID: string; canWrite: boolean }) {
  const { data, error, isLoading, isFetching, isSuccess } = useQuery(
    apartmentContentQuery(apartmentID),
  );
  const qc = useQueryClient();
  const { toast } = useToast();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFromDTO(undefined));
  const [saveErr, setSaveErr] = useState<string | null>(null);

  // Editing requires a loaded snapshot. A full-replace PUT built from an
  // undefined/empty draft would silently NULL every section, so we gate Edit on
  // a settled successful load (covers cold start AND background-refetch windows
  // that isLoading alone misses).
  const canEdit = isSuccess && !isFetching && data !== undefined;

  const save = useMutation({
    mutationFn: () =>
      putApartmentContent({
        throwOnError: true,
        path: { id: apartmentID },
        body: bodyFromDraft(draft, data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['apartments', apartmentID, 'content'] });
      toast({ kind: 'success', title: 'Content saved' });
      setEditing(false);
      setSaveErr(null);
    },
    onError: (e) => setSaveErr(errorMessage(e)),
  });

  const startEdit = () => {
    if (data === undefined) return; // never edit without a loaded snapshot
    setDraft(draftFromDTO(data));
    setSaveErr(null);
    setEditing(true);
  };

  const setText = (k: SectionKey, v: string) => setDraft((d) => ({ ...d, [k]: v }));
  const setOta = (k: OtaKey, v: boolean) => setDraft((d) => ({ ...d, [k]: v }));

  const loadErr = error ? errorMessage(error) : null;

  return (
    <section className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Content</h2>
        {!editing && canWrite && (
          <Button variant="secondary" onClick={startEdit} disabled={!canEdit}>
            Edit
          </Button>
        )}
        {editing && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {loadErr && (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {loadErr}
        </p>
      )}
      {saveErr && (
        <p role="alert" className="mb-3 text-sm text-destructive">
          {saveErr}
        </p>
      )}

      {editing ? (
        <div className="space-y-5">
          {SECTIONS.map(({ key, label }) => (
            <div key={key}>
              <MarkdownEditor
                id={`content-${key}`}
                label={label}
                value={draft[key]}
                onChange={(v) => setText(key, v)}
              />
              <label className="mt-1.5 flex items-center gap-2 text-xs text-muted">
                <input
                  type="checkbox"
                  checked={draft[`${key}_ota_sync`]}
                  onChange={(e) => setOta(`${key}_ota_sync`, e.target.checked)}
                />
                Sync to OTA listing
              </label>
            </div>
          ))}
        </div>
      ) : (
        <ContentReadView data={data} />
      )}
    </section>
  );
}

function ContentReadView({ data }: { data: ApartmentContentDTO | undefined }) {
  const authored = SECTIONS.filter(({ key }) => (data?.[key] ?? '').trim() !== '');

  if (authored.length === 0) {
    return <p className="text-sm text-muted">No content authored yet.</p>;
  }

  return (
    <div className="space-y-5">
      {authored.map(({ key, label }) => (
        <div key={key}>
          <div className="mb-1 flex items-center gap-2">
            <h3 className="text-xs uppercase tracking-[0.06em] text-muted">{label}</h3>
            {data?.[`${key}_ota_sync`] && (
              <span className="rounded bg-[var(--surface-sunken)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted">
                OTA
              </span>
            )}
          </div>
          <div className="prose prose-sm max-w-none text-sm text-foreground">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.[key] ?? ''}</ReactMarkdown>
          </div>
        </div>
      ))}
    </div>
  );
}
